import {
  CardShell,
  fixtureColourClass,
  PlayerPhoto,
  POSITION_LABELS,
  splitPlayerName,
  TeamBadgeSlot,
  TitleBar,
  venueLabel
} from './card-chrome';
import type { Player } from '@api-service/players';
import type { ComparisonRow } from '@lib/article-blocks/stats';
import type { GameweekWindowEntry } from '@lib/article-blocks/types';

type HeadToHeadCardProps = {
  playerOne: Player;
  playerTwo: Player;
  rows: ComparisonRow[];
  rangeLabel: string;
  upcomingOne: GameweekWindowEntry[];
  upcomingTwo: GameweekWindowEntry[];
};

const FixturePills = ({
  gameweeks,
  align
}: {
  gameweeks: GameweekWindowEntry[];
  align: 'left' | 'right';
}) => (
  <div
    className={`flex flex-wrap gap-1 ${
      align === 'right' ? 'justify-end' : 'justify-start'
    }`}
  >
    {gameweeks.flatMap(({ gameweek, fixtures }) =>
      fixtures.length === 0 ? (
        <span
          key={`blank-${gameweek}`}
          className="rounded-md bg-[#2C3134] px-2 py-0.5 text-[10px] font-bold text-[#7f8689]"
        >
          GW{gameweek} blank
        </span>
      ) : (
        fixtures.map((fixture) => (
          <span
            key={`${gameweek}-${fixture.opponent.shortName}`}
            className={`rounded-md px-2 py-0.5 text-[10px] font-bold text-[#faf5f5] ${fixtureColourClass(
              fixture
            )}`}
          >
            {fixture.opponent.shortName} ({venueLabel(fixture)})
          </span>
        ))
      )
    )}
  </div>
);

const PlayerSide = ({
  player,
  upcoming,
  align
}: {
  player: Player;
  upcoming: GameweekWindowEntry[];
  align: 'left' | 'right';
}) => {
  const { firstName, lastName } = splitPlayerName(player);
  const isRight = align === 'right';
  return (
    <div
      className={`flex min-w-0 flex-col gap-2 ${
        isRight ? 'items-end text-right' : 'items-start text-left'
      }`}
    >
      <div
        className={`flex items-center gap-3 ${
          isRight ? 'flex-row-reverse' : ''
        }`}
      >
        <PlayerPhoto player={player} sizeClass="h-14 w-14 sm:h-20 sm:w-20" />
        <div className="min-w-0">
          <div
            className={`flex items-center gap-2 text-xs leading-none text-[#fcfcfc] sm:text-sm ${
              isRight ? 'flex-row-reverse' : ''
            }`}
          >
            <TeamBadgeSlot shortName={player.team.shortName} />
            <span>{POSITION_LABELS[player.position]}</span>
            <span>£{player.price.toFixed(1)}m</span>
          </div>
          <h3 className="mt-0.5 truncate text-lg text-[#fcfcfc] sm:text-2xl">
            {firstName && <span>{firstName} </span>}
            <span className="font-bold">{lastName}</span>
          </h3>
        </div>
      </div>
      <FixturePills gameweeks={upcoming} align={align} />
    </div>
  );
};

export const HeadToHeadCard = ({
  playerOne,
  playerTwo,
  rows,
  rangeLabel,
  upcomingOne,
  upcomingTwo
}: HeadToHeadCardProps) => (
  <CardShell testId="head-to-head-card">
    <div className="grid min-w-0 grid-cols-2 gap-4 [&>*]:min-w-0">
      <PlayerSide player={playerOne} upcoming={upcomingOne} align="left" />
      <PlayerSide player={playerTwo} upcoming={upcomingTwo} align="right" />
    </div>
    <TitleBar title="Head to head" detail={rangeLabel} />
    <dl className="m-0 flex flex-col">
      {rows.map((row) => (
        <div
          key={row.label}
          className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-white/40 py-2"
        >
          <dd
            className={`m-0 text-left text-base sm:text-lg ${
              row.leader === 'left'
                ? 'font-black text-[#1FD0FF]'
                : 'font-semibold text-white'
            }`}
          >
            {row.left}
          </dd>
          <dt className="text-center text-xs text-[#a5aaad] sm:text-sm">
            {row.label}
          </dt>
          <dd
            className={`m-0 text-right text-base sm:text-lg ${
              row.leader === 'right'
                ? 'font-black text-[#1FD0FF]'
                : 'font-semibold text-white'
            }`}
          >
            {row.right}
          </dd>
        </div>
      ))}
    </dl>
  </CardShell>
);
